import React, { useState } from "react";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { TrendingUp, TrendingDown, Minus, Users, Sparkles } from "lucide-react";
import api from "@/api";

const RATING_STYLES = {
  strong_buy:  { bg: "rgba(0, 230, 118, 0.2)",  fg: "#00E676", label: "STRONG BUY" },
  buy:         { bg: "rgba(0, 230, 118, 0.1)",  fg: "#00E676", label: "BUY" },
  hold:        { bg: "rgba(255, 190, 0, 0.12)", fg: "#FFB800", label: "HOLD" },
  sell:        { bg: "rgba(255, 23, 68, 0.1)",  fg: "#FF1744", label: "SELL" },
  strong_sell: { bg: "rgba(255, 23, 68, 0.2)",  fg: "#FF1744", label: "STRONG SELL" },
  none:        { bg: "transparent",             fg: "#8B9BB4", label: "NO COVERAGE" },
};

function Bar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className="w-12" style={{ color: "var(--text-secondary)" }}>{label}</span>
      <div className="flex-1 h-1.5" style={{ background: "var(--border)" }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%" }} />
      </div>
      <span className="w-6 text-right font-mono">{count}</span>
    </div>
  );
}

function WhyButton({ ticker }) {
  const [state, setState] = useState({ open: false, loading: false, text: "", error: "" });

  const explain = async () => {
    setState({ open: true, loading: true, text: "", error: "" });
    try {
      const { data } = await api.post("/ai/analyst-reasoning", { ticker });
      setState({ open: true, loading: false, text: data.reasoning || "", error: "" });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setState({ open: true, loading: false, text: "", error: typeof detail === "string" ? detail : e.message });
    }
  };

  return (
    <div className="mt-3">
      {!state.open ? (
        <button
          data-testid={`ai-why-${ticker}`}
          className="btn-ghost inline-flex items-center gap-1 text-[10px]"
          onClick={explain}
        >
          <Sparkles size={10} style={{ color: "var(--gain)" }} /> Ask Claude why
        </button>
      ) : (
        <div className="text-[11px] p-3 leading-relaxed" data-testid={`ai-reasoning-${ticker}`}
             style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          {state.loading && <span style={{ color: "var(--text-secondary)" }}>Thinking<span className="blink">█</span></span>}
          {state.error && (
            <span style={{ color: "var(--loss)" }}>
              {state.error}
              <div className="text-[9px] mt-1 opacity-80">Top up your Emergent Universal Key if budget exceeded.</div>
            </span>
          )}
          {state.text && <p className="whitespace-pre-wrap">{state.text}</p>}
        </div>
      )}
    </div>
  );
}

export default function AnalystSignals({ holdings }) {
  const covered = (holdings || []).filter((h) => h.analyst_count > 0);
  const sorted = [...covered].sort((a, b) => (b.analyst_score || 0) - (a.analyst_score || 0));

  if (!covered.length) {
    return (
      <section data-testid="analyst-signals-section">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="label-xs mb-1">Wall Street</p>
            <h2 className="font-serif text-2xl">Analyst signals</h2>
          </div>
        </div>
        <div className="card text-xs py-8 text-center" style={{ color: "var(--text-secondary)" }}>
          No analyst coverage available yet for your holdings. Data is refreshed from Alpha Vantage every 24 hours.
        </div>
      </section>
    );
  }

  return (
    <section data-testid="analyst-signals-section">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="label-xs mb-1">Wall Street · consensus ratings</p>
          <h2 className="font-serif text-2xl">Analyst signals</h2>
        </div>
        <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
          {covered.length} covered · sorted by rating
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {sorted.map((h) => {
          const style = RATING_STYLES[h.analyst_rating_key] || RATING_STYLES.none;
          const upside = h.analyst_upside_pct;
          const upsideIcon = upside > 0 ? <TrendingUp size={12} /> : upside < 0 ? <TrendingDown size={12} /> : <Minus size={12} />;
          const upsideColor = upside > 0 ? "var(--gain)" : upside < 0 ? "var(--loss)" : "var(--text-secondary)";
          const bd = h.analyst_breakdown || {};
          return (
            <div
              key={h.id}
              data-testid={`analyst-card-${h.ticker}`}
              className="card p-4 hover:border-slate-600 transition-colors"
              style={{ borderColor: "var(--border)" }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-base">{h.ticker}</p>
                  <p className="text-[10px] truncate max-w-[180px]" style={{ color: "var(--text-secondary)" }}>
                    {h.name || ""}
                  </p>
                </div>
                <span
                  className="text-[10px] px-2 py-1 tracking-widest"
                  style={{ background: style.bg, color: style.fg, border: `1px solid ${style.fg}` }}
                  data-testid={`rating-badge-${h.ticker}`}
                >
                  {style.label}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="label-xs mb-1">Price target</p>
                  <p className="font-serif text-xl">{h.analyst_target ? fmtCurrency(h.analyst_target) : "—"}</p>
                </div>
                <div>
                  <p className="label-xs mb-1">Upside</p>
                  <p
                    className="font-serif text-xl inline-flex items-center gap-1"
                    style={{ color: upsideColor }}
                    data-testid={`upside-${h.ticker}`}
                  >
                    {upside != null ? (
                      <>{upsideIcon} {upside > 0 ? "+" : ""}{fmtPct(upside, 1)}</>
                    ) : "—"}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <Bar label="S.Buy"   count={bd.strong_buy}   total={h.analyst_count} color="#00E676" />
                <Bar label="Buy"     count={bd.buy}          total={h.analyst_count} color="#00b95d" />
                <Bar label="Hold"    count={bd.hold}         total={h.analyst_count} color="#FFB800" />
                <Bar label="Sell"    count={bd.sell}         total={h.analyst_count} color="#c62828" />
                <Bar label="S.Sell"  count={bd.strong_sell}  total={h.analyst_count} color="#FF1744" />
              </div>

              {/* Dividend outlook probability */}
              {h.div_prob && h.div_prob.label !== "No dividend" && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="label-xs">Dividend outlook · 12mo</p>
                    <span
                      className="text-[9px] tracking-wider uppercase"
                      style={{
                        color:
                          h.div_prob.raise >= 55 ? "var(--gain)" :
                          h.div_prob.cut >= 40 ? "var(--loss)" :
                          h.div_prob.cut >= 20 ? "#FFB800" : "var(--gain)",
                      }}
                      data-testid={`div-outlook-${h.ticker}`}
                    >
                      {h.div_prob.label}
                      {h.is_aristocrat && <span className="ml-1" title="Dividend Aristocrat">♛</span>}
                    </span>
                  </div>
                  <div className="flex h-2 overflow-hidden" style={{ background: "var(--border)" }}>
                    <div style={{ width: `${h.div_prob.cut}%`, background: "#FF1744" }} title={`Cut ${h.div_prob.cut}%`} />
                    <div style={{ width: `${h.div_prob.hold}%`, background: "#FFB800" }} title={`Hold ${h.div_prob.hold}%`} />
                    <div style={{ width: `${h.div_prob.raise}%`, background: "#00E676" }} title={`Raise ${h.div_prob.raise}%`} />
                  </div>
                  <div className="flex justify-between mt-1 text-[9px]" style={{ color: "var(--text-secondary)" }}>
                    <span style={{ color: "#FF1744" }} data-testid={`prob-cut-${h.ticker}`}>Cut {h.div_prob.cut}%</span>
                    <span style={{ color: "#FFB800" }}>Hold {h.div_prob.hold}%</span>
                    <span style={{ color: "#00E676" }} data-testid={`prob-raise-${h.ticker}`}>Raise {h.div_prob.raise}%</span>
                  </div>
                </div>
              )}

              <div className="mt-3 flex items-center gap-2 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                <Users size={10} />
                <span>{h.analyst_count} analysts</span>
                <span>·</span>
                <span>score {(h.analyst_score || 0).toFixed(2)} / 5</span>
                {h.analyst_source === "reference" && (
                  <span className="ml-auto text-[9px]" title="Reference consensus - will update when live coverage loads">
                    · ref
                  </span>
                )}
                {h.analyst_source === "live" && (
                  <span className="ml-auto text-[9px]" style={{ color: "var(--gain)" }}>· live</span>
                )}
              </div>

              <WhyButton ticker={h.ticker} />
            </div>
          );
        })}
      </div>

      <p className="text-[10px] mt-3" style={{ color: "var(--text-secondary)" }}>
        Consensus aggregated from Wall Street sell-side analysts via Alpha Vantage. Not financial advice.
      </p>
    </section>
  );
}
